

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const DayLineItemDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [lineItem, setLineItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/day-line-items/${id}`);
            setLineItem(response.data);
        } catch (error) {
            console.error('Error fetching day line item details:', error);
            setSnackbar({ open: true, message: 'Error loading day line item details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setLineItem(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/day-line-items/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Day Line Item updated successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            console.error('Error updating Day Line Item:', error);
            setSnackbar({ open: true, message: 'Error updating Day Line Item', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this Day Line Item?')) {
            try {
                await axiosInstance.delete(`/day-line-items/${id}`);
                setSnackbar({ open: true, message: 'Day Line Item deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/day-line-items'), 1000);
            } catch (error) {
                console.error('Error deleting Day Line Item:', error);
                setSnackbar({ open: true, message: 'Error deleting Day Line Item', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? lineItem.created_by : lineItem.updated_by, lineItem[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {lineItem[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={lineItem?.[item.field]}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                                multiline={item.multiline}
                                type={item.type}
                                options={item.options}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><Skeleton variant="rectangular" height={400} /></MDBox>
                
            </DashboardLayout>
        );
    }

    if (!lineItem) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Day Line Item not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    const typeOptions = [
        { value: 'Accommodation', label: 'Accommodation' },
        { value: 'Meal', label: 'Meal' },
        { value: 'Activity', label: 'Activity' },
        { value: 'Transfer', label: 'Transfer' },
        { value: 'Misc', label: 'Misc' }
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{lineItem.title || lineItem.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Day Line Item Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <IconButton onClick={() => { setIsEditing(false); setUpdatedFields({}); fetchData(); }} sx={commonIconButtonStyles}><Close sx={commonIconStyles} /></IconButton>
                                            <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}><Save sx={commonIconStyles} /></IconButton>
                                        </>
                                    ) : (
                                        <IconButton onClick={() => setIsEditing(true)} sx={commonIconButtonStyles}><Edit sx={commonIconStyles} /></IconButton>
                                    )}
                                    <IconButton onClick={handleDelete} sx={commonIconButtonStyles}><Delete sx={commonIconStyles} /></IconButton>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Title", field: "title" },
                                    { label: "Name", field: "name", readOnly: true },
                                    { label: "Type", field: "type", options: typeOptions },
                                    { label: "Itinerary Day ID", field: "itinerary_day_id" }, /* Ideally a select if editable, but simplistic for now */
                                    { label: "Trip Logistics ID", field: "trip_component_id" },
                                    { label: "Sort Order", field: "sort_order", type: "number" },
                                    { label: "Mandatory", field: "mandatory", type: "boolean", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => val ? "Yes" : "No" },
                                ])}

                                {renderSection("Scheduling & Logistics", [
                                    { label: "Start", field: "start", type: "datetime" },
                                    { label: "End", field: "end", type: "datetime" },
                                    { label: "Geo Location", field: "geo_location" },
                                    { label: "Min Pax", field: "min_pax", type: "number" },
                                    { label: "Max Pax", field: "max_pax", type: "number" },
                                ])}

                                {renderSection("Notes", [
                                    { label: "Notes", field: "notes", fullWidth: true, multiline: true },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default DayLineItemDetail;
